import { Injectable, signal, inject, OnDestroy, NgZone } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { Observable, of, throwError } from 'rxjs';
import { tap, catchError, switchMap } from 'rxjs/operators';
import { ToastrService } from 'ngx-toastr';

import { environment } from 'src/environments/environment';
import { User } from '../_helpers/user';
import { Role } from '../_helpers/role';

@Injectable({ providedIn: 'root' })
export class AuthenticationService implements OnDestroy {
  private router = inject(Router);
  private http = inject(HttpClient);
  private ngZone = inject(NgZone);
  private ws!: WebSocket;

  private currentUserSignal = signal<User | null>(null);
  private loadingSignal = signal<boolean>(false);
  private sessionInvalidHandled = false;

  isLogin: boolean = false;

  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;

  constructor(private toastr: ToastrService) {
    const userData = this.getStoredUser();

    if (userData.token) {
      this.isLogin = true;

      this.fetchCurrentUser().subscribe({
        next: () => {
          this.sessionInvalidHandled = false;
          this.connectWebSocket();
        },
        error: () => this.logout()
      });
    }
  }

  private getStoredUser(): any {
    try {
      return JSON.parse(localStorage.getItem('currentUser') || '{}');
    } catch {
      return {};
    }
  }

  handleSessionInvalid(): void {
    if (this.sessionInvalidHandled) return;

    this.sessionInvalidHandled = true;
    this.toastr.warning('Your account was logged in somewhere else.', 'Session Ended');
    this.Autologout();
  }

  private connectWebSocket(): void {
    const token = this.getToken();
    if (!token) return;
    if (!localStorage.getItem('browser_id')) {
      localStorage.setItem('browser_id', crypto.randomUUID());
    }
    const browserId = localStorage.getItem('browser_id');

    this.ws = new WebSocket(`${environment.wsUrl}/ws/user/?admin_token=${token}&browser_id=${browserId}`);

    this.ws.onopen = () => {
      this.reconnectAttempts = 0;
    };

    this.ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        if (data.type === 'FORCE_LOGOUT') {
          this.ngZone.run(() => this.handleSessionInvalid());
        }
      } catch (err) {
        console.error('WebSocket message parse error:', err);
      }
    };

    this.ws.onclose = () => {
      if (this.isLogin && this.reconnectAttempts < this.maxReconnectAttempts) {
        this.reconnectAttempts++;
        setTimeout(() => this.connectWebSocket(), 5000);
      }
    };

    this.ws.onerror = (err) => {
      console.error('WebSocket error:', err);
      this.ws.close();
    };
  }

  private disconnectWebSocket(): void {
    if (this.ws) {
      this.ws.onclose = null;
      this.ws.close();
      this.ws = undefined!;
    }
  }

  fetchCurrentUser(): Observable<User> {
    this.loadingSignal.set(true);

    const userData = this.getStoredUser();

    if (userData.token) {
      this.currentUserSignal.set(userData);
      this.loadingSignal.set(false);
      return of(userData);
    } else {
      this.loadingSignal.set(false);
      this.logout();
      return throwError(() => new Error('No user found'));
    }
  }

  get isLoading(): boolean {
    return this.loadingSignal();
  }

  public get currentUserValue(): User | null {
    return this.currentUserSignal();
  }

  get currentUserName(): string | null {
    return this.currentUserSignal()?.user?.username || null;
  }
  login(identifier: string, password: string) {
    return this.http.post<User>(`${environment.apiUrl}/api/admin/login/`, { identifier, password }).pipe(
      switchMap((data: User) => {
        const userDetails = {
          user: {
            id: data.user.id,
            email: data.user.email,
            role: Role.SuperAdmin
          },
          token: data.token,
          refresh_token: data.refresh_token
        };

        localStorage.setItem('currentUser', JSON.stringify(userDetails));
        this.sessionInvalidHandled = false;

        return this.fetchCurrentUser().pipe(
          tap(() => {
            this.isLogin = true;
            this.connectWebSocket();
            this.toastr.success('Login successful!', 'Success');
          })
        );
      })
    );
  }

  isLoggedIn() {
    return this.isLogin;
  }

  logout() {
    this.disconnectWebSocket();
    this.sessionInvalidHandled = false;

    this.http.post<any>(`${environment.apiUrl}/api/admin/logout/`, {}).subscribe({
      error: () => {}
    });

    localStorage.removeItem('currentUser');
    localStorage.removeItem('browser_id');
    this.isLogin = false;
    this.currentUserSignal.set(null);
    this.loadingSignal.set(false);
    this.router.navigate(['/login']);
  }

  Autologout() {
    this.disconnectWebSocket();

    localStorage.removeItem('currentUser');
    this.isLogin = false;
    this.currentUserSignal.set(null);
    this.loadingSignal.set(false);
    this.router.navigate(['/login']);
  }

  refreshToken(): Observable<any> {
    const userData = this.getStoredUser();

    if (!userData.refresh_token) {
      return throwError(() => new Error('Invalid User'));
    }

    return this.http
      .post<any>(`${environment.apiUrl}/api/admin/refresh-token/`, {
        refresh_token: userData.refresh_token
      })
      .pipe(
        tap((response) => {
          if (response.token) userData.token = response.token;
          if (response.refresh_token) userData.refresh_token = response.refresh_token;

          localStorage.setItem('currentUser', JSON.stringify(userData));
        }),
        catchError((error) => {
          console.error('Refresh token failed', error);
          return throwError(() => error);
        })
      );
  }

  getToken(): string | null {
    return this.getStoredUser().token || null;
  }

  register(id: string, email: string, password: string, firstName?: string, lastName?: string): Observable<User> {
    return this.http
      .post<User[]>(`${environment.apiUrl}/api/account/register`, {
        id,
        email,
        password,
        firstName,
        lastName
      })
      .pipe(
        switchMap((response) => {
          const users: User[] = Array.isArray(response) ? response : [response];
          const registeredUser = users.find((u: any) => u.email === email);

          if (!registeredUser) {
            throw new Error('Registration successful but user not found');
          }

          return this.login(email, password);
        }),
        catchError((error) => {
          console.error('Registration error:', error);
          throw error;
        })
      );
  }

  ngOnDestroy(): void {
    this.disconnectWebSocket();
  }
}
